import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormContainer, SubHeading, Heading, FlexContainer, SubHeadingContainer } from './QuestionsStyle'
import QuestionItem, { QuestionData } from './QuestionItem'
import styled from 'styled-components'
import SubmitButton from 'uiKit/buttons/SubmitButton'
import { AddSectionIcon, ErrorSignIcon } from 'uiKit/icons/Icons'
import NewSection from './NewSection'
import { validateSectionAndFlag, sectionHasErrors as sectionHasErrorsUtil } from './validationUtils'
import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  IconButton,
  withStyles,
  Popover,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import { getSectionOptions } from '../../../api/supervision'
import {
  addQuestion,
  deleteQuestion,
  updateQuestion,
  setSectionOptions,
  addSection,
  deleteSection,
  setExpandedSection,
  setIsFetchingSections,
} from '../../../reducers/questions'

const PopoverButton = styled.button`
  background: transparent;
  border: none;
  padding: 10px 16px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'Lato';
  font-size: 14px;
  color: ${props => (props.hasAttentionStyles ? 'var(--color-text-error)' : 'var(--color-text-primary)')};
  &:hover {
    background-color: var(--color-selago-grey);
  }
`

const styles = {
  popover: {
    borderRadius: '10px',
    boxShadow: '0px 4px 29px rgba(19, 69, 186, 0.0855129)',
    padding: '8px 0',
    minWidth: '160px',
  },
}

const StyledExpansionPanelSummary = withStyles({
  root: {
    minHeight: '56px',
    '&$expanded': {
      minHeight: '56px',
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    margin: '0',
    '&$expanded': {
      margin: '0',
    },
    '& > :last-child': {
      paddingRight: 0,
    },
  },
  expanded: {},
})(ExpansionPanelSummary)

const AddQuestions: React.FC<{ botId: number; isDisabled?: boolean }> = ({ botId, isDisabled }) => {
  const dispatch = useDispatch()

  const questionsBySection = useSelector((state: any) => state.questions.questionsBySection)
  const sections = useSelector((state: any) => state.questions.sections)
  const sectionOptions = useSelector((state: any) => state.questions.sectionOptions)
  const expandedSection = useSelector((state: any) => state.questions.expandedSection)
  const isFetchingSections = useSelector((state: any) => state.questions.isFetchingSections)

  const [newSectionModalOpen, setNewSectionModalOpen] = useState(false)
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const getCurrentSectionQuestions = (sectionId: string): QuestionData[] => {
    return questionsBySection[sectionId] || []
  }

  const createDefaultQuestion = (): QuestionData => {
    return {
      id: `question_1_${Date.now()}`,
      question: '',
      type: 'free_text',
      isRequired: true,
      options: [
        { id: `option_1_${Date.now()}`, value: '' },
        { id: `option_2_${Date.now()}`, value: '' },
      ],
      correctAnswers: [],
    }
  }

  const handleAddQuestion = (sectionId: string) => {
    if (!sectionId) return
    const sectionQuestions = getCurrentSectionQuestions(sectionId)

    if (sectionQuestions.length > 0) {
      const allValid = validateSectionAndFlag(sectionQuestions as any, sectionId, dispatch)
      if (!allValid) return
    }

    const newQuestion = createDefaultQuestion()
    newQuestion.id = `question_${sectionQuestions.length + 1}_${Date.now()}`
    dispatch(addQuestion(newQuestion, sectionId))
  }

  const handleDeleteQuestion = (sectionId: string, index: number) => {
    if (!sectionId) return

    const sectionQuestions = getCurrentSectionQuestions(sectionId)
    const questionId = sectionQuestions[index].id
    dispatch(deleteQuestion(questionId, sectionId))
  }

  const handleQuestionChange = (sectionId: string, questionIndex: number, questionData: any) => {
    if (!sectionId) return
    const { _validationError, ...cleanData } = questionData
    dispatch(updateQuestion(cleanData, sectionId))
  }

  const handleAddNewSection = () => {
    if (sections.length === 0) {
      setNewSectionModalOpen(true)
      return
    }
    for (let i = 0; i < sections.length; i++) {
      const sectionId = `section-${i}-${sections[i].value}`
      if (questionsBySection.hasOwnProperty(sectionId)) {
        const sectionQuestions = questionsBySection[sectionId] || []
        // If a section has zero questions, block modal and focus user on that section
        if (sectionQuestions.length === 0) {
          setLocalExpandedSection(sectionId)
          dispatch(setExpandedSection(sectionId))
          return
        }
        if (sectionQuestions.length > 0) {
          const allValid = validateSectionAndFlag(sectionQuestions as any, sectionId, dispatch)
          if (!allValid) return
        }
      }
    }
    setNewSectionModalOpen(true)
  }

  useEffect(() => {
    if (newSectionModalOpen && sectionOptions?.length === 0) {
      fetchSectionOptions()
    }
  }, [newSectionModalOpen, sectionOptions?.length])

  const fetchSectionOptions = () => {
    dispatch(setIsFetchingSections(true))
    getSectionOptions(botId).then(response => {
      const formattedOptions = response.data
        .filter(section => section.name.trim() !== 'Other')
        .sort((a, b) => Number(a.code) - Number(b.code))
        .map(section => ({
          value: section.id,
          label: section.name,
        }))
      dispatch(setSectionOptions(formattedOptions))
      dispatch(setIsFetchingSections(false))
    })
  }

  const handleSaveSection = (sectionName: { value: string; label: string }) => {
    const sectionExists = sections.some(section => section.value === sectionName.value)

    if (sectionExists) {
      return
    }

    dispatch(addSection(sectionName))

    const newSectionIndex = sections.length
    const newSectionId = `section-${newSectionIndex}-${sectionName.value}`
    setLocalExpandedSection(newSectionId)
    dispatch(setExpandedSection(newSectionId))
    setNewSectionModalOpen(false)
  }

  const handleSectionAction = (action: string, sectionId: string) => {
    setPopoverAnchorEl(null)
    setActiveSection(null)

    const sectionIndex = sections.findIndex((_, index) => `section-${index}-${sections[index].value}` === sectionId)

    if (sectionIndex === -1) return

    if (action === 'delete') {
      dispatch(deleteSection(sectionIndex))
    }
  }

  const [localExpandedSection, setLocalExpandedSection] = useState<string | null>(null)

  useEffect(() => {
    setLocalExpandedSection(expandedSection)
  }, [expandedSection])

  const handleSectionExpand = (sectionId: string, expanded: boolean) => {
    if (popoverAnchorEl) return
    setLocalExpandedSection(expanded ? sectionId : null)
    setTimeout(() => {
      dispatch(setExpandedSection(expanded ? sectionId : null))
    }, 0)
  }

  if (isFetchingSections) {
    return (
      <FormContainer>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>Loading sections...</div>
      </FormContainer>
    )
  }

  return (
    <FormContainer style={{ maxWidth: '1042px' }}>
      <SubHeadingContainer>
        <div>
          <Heading>Add questions</Heading>
          <SubHeading>
          You need to add at least one question to each section you create for this Supervision/Appraisal/Survey.
          </SubHeading>
        </div>
        <FlexContainer>
          <SubmitButton
            icon={<AddSectionIcon />}
            styles={{ width: 194 }}
            type="primary_with_border"
            title="Add a section"
            disabled={isDisabled}
            onClick={handleAddNewSection}
          />
        </FlexContainer>
      </SubHeadingContainer>
      {sections.length > 0 &&
        sections.map((sectionName, index) => {
          const sectionId = `section-${index}-${sectionName.value}`
          const isExpanded = expandedSection === sectionId

          return (
            <ExpansionPanel
              key={sectionId}
              expanded={localExpandedSection === sectionId}
              onChange={(event, expanded) => {
                handleSectionExpand(sectionId, expanded)
              }}
              style={{
                borderRadius: '10px',
                border: '1px solid var(--color-issabeline-grey)',
                overflow: 'hidden',
                boxShadow: 'none',
                margin: '15px 0',
              }}>
              <StyledExpansionPanelSummary
                classes={{
                  content: 'expansionPanelSummaryContent',
                  expanded: 'expansionPanelSummaryExpanded',
                }}
                style={{
                  backgroundColor: 'var(--color-selago-grey)',
                  borderBottom: isExpanded ? '1px solid var(--color-issabeline-grey)' : 'none',
                  padding: '0 16px',
                }}>
                <FlexContainer>
                  {isExpanded ? (
                    <ExpandMoreIcon style={{ transform: 'rotate(180deg)', color: 'var(--color-dark-gray)' }} />
                  ) : (
                    <ExpandMoreIcon style={{ color: 'var(--color-dark-gray)' }} />
                  )}
                  <Typography
                    style={{
                      fontWeight: 500,
                      fontSize: '18px',
                      marginLeft: '10px',
                      color: 'var(--color-charcoal)',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    {sectionName?.label}
                    {sectionHasErrorsUtil(getCurrentSectionQuestions(sectionId) as any) ? (
                      <>
                        {' '}
                        <ErrorSignIcon />{' '}
                        <span style={{ color: 'var(--color-text-error)', fontWeight: 500, fontSize: '16px' }}>
                          {' '}
                          Error
                        </span>
                      </>
                    ) : null}
                  </Typography>
                </FlexContainer>
                <FlexContainer style={{ paddingRight: 0 }}>
                  <Typography
                    style={{
                      marginRight: '12px',
                      fontSize: '18px',
                      color: 'var(--color-dark-gray)',
                      fontWeight: 500,
                    }}>
                    {questionsBySection[sectionId]?.length || 0}{' '}
                    {questionsBySection[sectionId]?.length === 1 ? 'question' : 'questions'}
                  </Typography>
                  {isDisabled ? null : <IconButton
                    style={{ padding: '4px' }}
                    onClick={e => {
                      e.stopPropagation()
                      setPopoverAnchorEl(e.currentTarget)
                      setActiveSection(sectionId)
                    }}>
                    <MoreVertIcon style={{ color: 'var(--color-dark-gray)' }} />
                  </IconButton>}
                  <Popover
                    open={Boolean(popoverAnchorEl) && activeSection === sectionId}
                    anchorEl={popoverAnchorEl}
                    onClose={() => {
                      setPopoverAnchorEl(null)
                      setActiveSection(null)
                    }}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      style: styles.popover,
                    }}>
                    {isDisabled ? null : (
                      <PopoverButton hasAttentionStyles onClick={() => handleSectionAction('delete', sectionId)}>
                        Delete section
                      </PopoverButton>
                    )}
                  </Popover>
                </FlexContainer>
              </StyledExpansionPanelSummary>
              <ExpansionPanelDetails
                style={{
                  padding: '20px',
                  backgroundColor: 'white',
                  display: 'block',
                  overflowY: 'auto',
                }}>
                <div>
                  {localExpandedSection === sectionId &&
                    getCurrentSectionQuestions(sectionId).map((question, index) => (
                      <QuestionItem
                        key={question.id}
                        index={index + 1}
                        questionData={question}
                        disabled={isDisabled}
                        onDelete={
                          getCurrentSectionQuestions(sectionId).length > 0
                            ? () => handleDeleteQuestion(sectionId, index)
                            : undefined
                        }
                        onChange={data => handleQuestionChange(sectionId, index, data)}
                      />
                    ))}
                  <FlexContainer>
                    <SubmitButton
                      icon={<AddCircleOutlineIcon />}
                      styles={{ width: 194 }}
                      type="primary_with_border"
                      title="Add a question"
                      onClick={() => handleAddQuestion(sectionId)}
                      disabled={isDisabled}
                    />
                  </FlexContainer>
                </div>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          )
        })}
      <NewSection
        isOpen={newSectionModalOpen}
        onClose={() => setNewSectionModalOpen(false)}
        onSave={handleSaveSection}
        sectionOptions={sectionOptions}
        sections={sections}
      />
    </FormContainer>
  )
}

export default AddQuestions
